import { createFileRoute } from '@tanstack/react-router'
import { Button, cx } from '@tutlo/ui'
import type { ReactNode } from 'react'
import { AskFooter } from '../layout.tsx'
import { Hook } from '../screens/hook.tsx'
import { Assurances, Check, StartError, useLandingStart } from '../screens/landing.tsx'
import { validateSearch } from '../search.ts'

export const Route = createFileRoute('/g')({
  validateSearch,
  component: GoogleLanding,
})

/**
 * Google Search landing. The first screen is the Hook from `/` with a search-keyword headline; below the fold sits the content of
 * tutlo.com's beginner course page phrase for phrase — the search keywords drive the ads'
 * landing-page relevance — with every CTA leading into the same funnel as `/`. The server sends
 * noindex for this path (landing-head.ts) so it never competes with that page.
 */
function GoogleLanding() {
  const landing = useLandingStart(Route.useSearch())
  const { start, openChat, starting, startFailed } = landing

  const cta = (
    <div className="mx-auto flex w-full max-w-[448px] flex-col gap-md">
      <Button onClick={start} loading={starting}>
        Umów bezpłatną lekcję próbną
      </Button>
      <Assurances />
      {startFailed ? <StartError /> : null}
    </div>
  )

  return (
    <>
      {/* Search-keyword headline: the ads' landing-page relevance depends on it. */}
      <Hook
        {...landing}
        endsPage={false}
        barWithChat
        headline={{ lead: 'Angielski dla początkujących', highlight: 'kurs online' }}
      />

      {/* Below the fold: tutlo.com's course page as full-width bands. Bottom padding keeps the last
          band clear of the sticky bar on mobile. */}
      <div className="bg-bg-card pb-[104px] desktop:pb-0">
        <section
          aria-label="Tutlo w liczbach"
          className="px-base py-xl desktop:px-2xl desktop:py-2xl"
        >
          <dl className="mx-auto grid max-w-[1100px] grid-cols-2 gap-x-base gap-y-lg rounded-card-xl bg-[color-mix(in_srgb,var(--color-action)_28%,white)] px-base py-lg shadow-card desktop:grid-cols-4 desktop:py-xl">
            {STATS.map(([value, label]) => (
              <div key={label} className="flex flex-col-reverse items-center gap-[2px] text-center">
                <dt className="text-label text-text-secondary">{label}</dt>
                <dd className="font-extrabold text-[32px] text-text-primary leading-tight tracking-[-0.03em] desktop:text-[44px]">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <Band
          eyebrow="Angielski dla początkujących online"
          title="Kurs angielskiego dla początkujących w Tutlo"
        >
          <Lead>
            Zacznij od podstaw i krok po kroku rozwijaj swoje umiejętności – w swoim tempie, bez
            presji i stresu. W Tutlo oferujemy kursy angielskiego dla początkujących, które
            prowadzone są indywidualnie i skupiają się na praktycznej komunikacji od pierwszych
            lekcji. To idealny sposób, aby uczyć się angielskiego od podstaw – zwłaszcza jeśli boisz
            się mówić lub nie wiesz, od czego zacząć.
          </Lead>
        </Band>

        <Band tone="muted" title="Dlaczego warto uczyć się z nami?">
          <Cards
            columns={4}
            items={[
              [
                'Twój sukces to nasza misja',
                'Indywidualne podejście i personalizowane lekcje angielskiego dla początkujących online, dostosowane do Twojego poziomu.',
              ],
              [
                'Bez presji, bez stresu',
                'Ucz się w dogodnym tempie z polskimi lektorami, z miejsca, w którym czujesz się swobodnie.',
              ],
              [
                'Twoje cele, nasze wsparcie',
                'Podczas kursu angielskiego dla początkujących pomagamy Ci przejść drogę od podstaw do płynności.',
              ],
              [
                'Pełna elastyczność',
                'Z naszymi lektorami połączysz się codziennie o dowolnej porze między 7:00 a 22:00.',
              ],
            ]}
          />
        </Band>

        <Band title="Angielski dla początkujących – kurs dopasowany do Ciebie">
          <div className="flex flex-col gap-base">
            <Lead>
              Rozpocznij swoją przygodę z internetowym kursem angielskiego dla początkujących od
              darmowej lekcji próbnej i przekonaj się, jak skuteczna i przyjazna jest nasza metoda.
            </Lead>
            <Lead>
              Niezależnie od tego, czy dopiero zaczynasz, czy chcesz rozwijać już zdobyte
              umiejętności, dopasujemy kurs idealnie do Twojego poziomu.
            </Lead>
            <Lead>
              Ucz się tak, jak lubisz – kurs angielskiego online dla początkujących możesz
              realizować w dowolnym miejscu i czasie, korzystając z komputera, tabletu lub telefonu.
            </Lead>
            <Lead>
              Na każdym etapie możesz liczyć na wsparcie doświadczonych polskich lektorów oraz
              nowoczesne, interaktywne materiały, które pomogą Ci śledzić postępy i skutecznie
              osiągać wyznaczone cele.
            </Lead>
          </div>
          {cta}
        </Band>

        <Band tone="muted" title="Angielski dla początkujących online – jak rozpocząć naukę?">
          <ol className="grid gap-sm desktop:grid-cols-3">
            <Step n={1} title="Opowiedz nam o sobie">
              Odpowiedz na 5 krótkich pytań o cel, poziom i grafik. Dzięki temu przewodnik od razu
              wie, jaki kurs angielskiego od podstaw Ci dopasować – nie musisz opowiadać wszystkiego
              od zera.
            </Step>
            <Step n={2} title="Odbierz lekcję próbną">
              Wybierz dogodny termin z najbliższych dni. Spotkanie online trwa około 30 minut i jest
              zupełnie bez zobowiązań: poznasz platformę, porozmawiasz z lektorem i przekonasz się,
              czy taka forma nauki Ci odpowiada.
            </Step>
            <Step n={3} title="Dostosuj swój plan nauki">
              Po lekcji próbnej nie zostajesz sam(a). Przewodnik pomoże Ci dopasować kurs
              angielskiego dla początkujących online do Twoich możliwości i tempa życia, ułoży plan
              nauki i poda konkretną cenę. Decyzja należy do Ciebie – to również nic nie kosztuje.
            </Step>
          </ol>
          {cta}
        </Band>

        <Band title="Sprawdź, czy jesteś początkujący!">
          <Lead>
            Zastanawiasz się, czy pamiętasz podstawy angielskiego? Na lekcji próbnej lektor sprawdzi
            Twój poziom i pomoże precyzyjnie dopasować kurs językowy do Twoich potrzeb oraz celów.
            Kurs angielskiego od podstaw to dobry pomysł dla wszystkich, którzy dopiero rozpoczynają
            swoją przygodę z nauką oraz dla tych, którzy potrzebują jedynie odświeżenia wiedzy.
          </Lead>
        </Band>

        <Band
          tone="muted"
          eyebrow="Wygodnie i elastycznie"
          title="Kursy języka angielskiego dla początkujących"
        >
          <Lead>
            Kursy angielskiego dla początkujących w Tutlo to szeroka i różnorodna oferta, dzięki
            której z łatwością znajdziesz rozwiązanie dopasowane do swoich potrzeb i celów.
            Niezależnie od tego, czy chcesz nauczyć się podstaw do pracy, podróży, czy codziennej
            komunikacji, możesz wybrać kurs idealnie odpowiadający Twoim oczekiwaniom. Sprawdź
            dostępne możliwości i wybierz kurs angielskiego online dla początkujących, który
            najlepiej odpowiada Twoim planom – rozpoczęcie nauki jeszcze nigdy nie było tak proste.
          </Lead>
          <div className="grid items-start gap-sm desktop:grid-cols-2">
            {COURSES.map((course) => (
              <Disclosure
                key={course.name}
                summary={
                  <span className="flex flex-col gap-[4px]">
                    {course.popular ? (
                      <span className="w-fit rounded-pill bg-selected-soft px-sm py-[2px] font-bold text-[12px] text-selected">
                        Najczęściej wybierany
                      </span>
                    ) : null}
                    <span className="text-body-strong text-text-primary">{course.name}</span>
                    <span className="text-label text-text-secondary">{course.level}</span>
                  </span>
                }
              >
                {course.text}
              </Disclosure>
            ))}
          </div>
          <Lead>
            Kursy z lektorem opierają się na autorskiej metodzie True-Life English 360° Adults.
            Każda lekcja składa się z 3 części: pre-teaching – krótkie wideo wprowadzające i 3
            ćwiczenia interaktywne; lekcja z lektorem – 20-minutowa lekcja na żywo z lektorem
            online; self-study – fiszki i ćwiczenia interaktywne do utrwalenia materiału w dowolnym
            czasie. …i kolejne z ponad 70 autorskich kursów online.
          </Lead>
        </Band>

        <Band title="Zobacz jak zaczynali inni">
          <Lead>
            Każdy kiedyś zaczynał. Nasi kursanci często przyznają, że myśleli, iż nauka angielskiego
            dla początkujących i kurs w profesjonalnej szkole są zbyt skomplikowane lub nie dla
            nich. Dzięki elastycznej formule Tutlo szybko przełamali barierę i dziś zmienili pracę
            na lepszą, swobodnie oglądają filmy w oryginale, podróżują i rozmawiają z
            obcokrajowcami. Angielski dla początkujących online nie musi być nudny. U nas uczysz się
            przez rozmowę, nie przez wkuwanie.
          </Lead>
        </Band>

        <Band tone="muted" title="Angielski dla początkujących online – co nas wyróżnia?">
          <Lead>
            U nas nie ma przypadkowego programu. Oferujemy dobrze zaprojektowany kurs nauki języka
            angielskiego dla początkujących – podstawy z jasno określonymi celami, dzięki czemu
            szybko zauważysz postępy.
          </Lead>
          <Cards
            columns={3}
            items={[
              [
                'Lekcje 1:1',
                'Angielskiego dla początkujących online uczysz się z lektorem, który prowadzi Cię od pierwszej lekcji i wspiera w przełamywaniu bariery językowej.',
              ],
              [
                'Jasna ścieżka nauki',
                'Zasady kursu są jasne – wiesz, czego się uczysz i po co. Masz kontrolę nad swoimi postępami oraz dostęp do materiałów do powtórek.',
              ],
              [
                'Maksymalna elastyczność',
                'Uczysz się rano, wieczorem albo między obowiązkami, z dowolnego miejsca przy użyciu telefonu, laptopa czy tabletu.',
              ],
            ]}
          />
        </Band>

        <Band title="Kurs języka angielskiego dla początkujących online – sprawdź opinie naszych uczniów">
          <ul className="grid gap-sm tablet:grid-cols-2 desktop:grid-cols-3">
            {REVIEWS.map(([quote, name, role, photo]) => (
              <li
                key={name}
                className="flex flex-col justify-between gap-lg rounded-card-xl bg-bg-card p-lg shadow-card"
              >
                <p className="text-body text-text-secondary">„{quote}”</p>
                <div className="flex items-center gap-md">
                  <img
                    src={`/reviews/${photo}.jpg`}
                    alt=""
                    width={48}
                    height={48}
                    loading="lazy"
                    className="size-12 shrink-0 rounded-pill border-2 border-text-primary object-cover"
                  />
                  <p className="flex flex-col">
                    <span className="text-body-strong text-text-primary">{name}</span>
                    <span className="text-label text-text-secondary">{role}</span>
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-center text-label text-text-secondary">
            Opinie nie są weryfikowane pod kątem ich pochodzenia od konsumentów, którzy używali
            produktu lub go nabyli.
          </p>
        </Band>

        <Band
          tone="muted"
          title="Najczęściej zadawane pytania o kurs angielskiego dla początkujących"
        >
          <div className="mx-auto flex w-full max-w-[760px] flex-col gap-sm">
            {FAQ.map(([question, answer]) => (
              <Disclosure
                key={question}
                summary={<span className="text-body-strong text-text-primary">{question}</span>}
              >
                {answer}
              </Disclosure>
            ))}
          </div>
        </Band>

        <Band title="Umów się na darmową lekcję próbną">
          <Lead>Zapis zajmuje około 2 minut i do niczego nie zobowiązuje.</Lead>
          {cta}
          <AskFooter onOpen={openChat} />
        </Band>
      </div>
    </>
  )
}

/** Full-width band with a centred column, like the sections of tutlo.com's course pages. */
function Band({
  eyebrow,
  title,
  tone = 'plain',
  children,
}: {
  eyebrow?: string
  title: string
  tone?: 'plain' | 'muted'
  children: ReactNode
}) {
  return (
    <section className={tone === 'muted' ? 'bg-bg' : 'bg-bg-card'}>
      <div className="mx-auto flex max-w-[1100px] flex-col gap-lg px-base py-2xl desktop:px-2xl desktop:py-[72px]">
        <div className="mx-auto flex max-w-[760px] flex-col items-center gap-sm text-center">
          {eyebrow ? (
            <p className="text-label-strong text-text-secondary uppercase tracking-[0.08em]">
              {eyebrow}
            </p>
          ) : null}
          <h2 className="text-h2 text-text-primary desktop:text-[32px]">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  )
}

function Lead({ children }: { children: ReactNode }) {
  return (
    <p className="mx-auto max-w-[760px] text-center text-body text-text-secondary desktop:text-body-lg">
      {children}
    </p>
  )
}

function Cards({ items, columns }: { items: Array<[string, string]>; columns: 3 | 4 }) {
  return (
    <div
      className={cx(
        'grid gap-sm tablet:grid-cols-2',
        columns === 4 ? 'desktop:grid-cols-4' : 'desktop:grid-cols-3',
      )}
    >
      {items.map(([title, text]) => (
        <div
          key={title}
          className="flex flex-col gap-sm rounded-card-lg border border-border-soft bg-bg-card p-base shadow-soft"
        >
          <Check className="size-5 text-success" />
          <h3 className="text-h3 text-text-primary">{title}</h3>
          <p className="text-body text-text-secondary">{text}</p>
        </div>
      ))}
    </div>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="flex flex-col gap-sm rounded-card-lg border border-border-soft bg-bg-card p-base shadow-soft">
      <span className="flex size-9 items-center justify-center rounded-pill bg-selected-soft text-label-strong text-selected">
        {n}
      </span>
      <h3 className="text-h3 text-text-primary">{title}</h3>
      <p className="text-body text-text-secondary">{children}</p>
    </li>
  )
}

/** Native details/summary: the answers stay in the page text (keywords) while it reads short. */
function Disclosure({ summary, children }: { summary: ReactNode; children: ReactNode }) {
  return (
    <details className="group rounded-card-lg border border-border-soft bg-bg-card px-base py-md shadow-soft">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-md [&::-webkit-details-marker]:hidden">
        {summary}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-5 shrink-0 text-text-secondary transition-transform duration-150 group-open:rotate-180"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <p className="pt-sm text-body text-text-secondary">{children}</p>
    </details>
  )
}

const STATS = [
  ['9+', 'lat doświadczenia'],
  ['70+', 'autorskich kursów online'],
  ['4000+', 'lektorów z całego świata'],
  ['80 000+', 'uczniów'],
] as const

const COURSES = [
  {
    name: 'New Travel English for Beginners',
    level: 'Poziom podstawowy A1 · lekcje indywidualne',
    popular: true,
    text: 'Kurs New Travel English for Beginners jest idealny dla osób, które chcą rozpocząć przygodę z językiem angielskim i nauczyć się używać go podczas zagranicznych wyjazdów. Kurs składa się z 40 lekcji i obejmuje podstawowe zagadnienia związane z podróżowaniem.',
  },
  {
    name: 'New Beginner General English',
    level: 'Poziom podstawowy Pre-A1 · lekcje indywidualne',
    popular: false,
    text: 'New Beginner General English to indywidualny kurs angielskiego dla dorosłych, którzy nigdy wcześniej nie uczyli się języka angielskiego bądź mieli bardzo długą przerwę w nauce i chcieliby zacząć od podstaw. Kurs obejmuje naukę słówek, użytecznych zwrotów i podstaw gramatyki.',
  },
  {
    name: 'New Grammar for Beginners',
    level: 'Poziom podstawowy Pre-A1 · lekcje indywidualne',
    popular: false,
    text: 'Kurs New Grammar for Beginners składa się z 50 lekcji i jest przeznaczony dla osób początkujących, które chcą solidnie zrozumieć podstawy gramatyki angielskiej. Każda lekcja koncentruje się na konkretnym zagadnieniu. Kurs obejmuje tematy takie jak czasy gramatyczne, budowa zdania, pytania oraz inne podstawowe struktury.',
  },
  {
    name: 'New Conversational English',
    level: 'Poziom zróżnicowany · lekcje indywidualne',
    popular: false,
    text: 'Kurs New Conversational English został stworzony dla osób, które pragną rozwijać swoje umiejętności konwersacyjne w różnorodnych sytuacjach. Kurs składa się z 50 lekcji i skupia się na praktycznych zwrotach, idiomach i tematach związanych z życiem codziennym, kulturą oraz aktualnościami.',
  },
  {
    name: 'New Pre-Intermediate General English',
    level: 'Poziom podstawowy A1 · lekcje indywidualne',
    popular: false,
    text: 'New Pre-Intermediate General English to kontynuacja kursu podstawowego lub start dla dorosłych, którzy są już na poziomie A1/A2. Kurs skupia się na rozszerzaniu słownictwa oraz struktur gramatycznych.',
  },
  {
    name: 'New Intermediate General English',
    level: 'Poziom średniozaawansowany B1 · lekcje indywidualne',
    popular: false,
    text: 'New Intermediate General English to kontynuacja rozszerzania słownictwa i struktur gramatycznych wprowadzonych na poprzednich poziomach. Kurs wprowadza zagadnienia w oparciu o metodę komunikacyjną, a uczeń jest zachęcany do rozmowy. Zakres materiału obejmuje m.in. hobby, pracę, sztukę czy sport, rozwijając umiejętności dyskusyjne w szerokim zakresie tematów.',
  },
  {
    name: 'New Intermediate Grammar',
    level: 'Poziom zróżnicowany · lekcje indywidualne',
    popular: false,
    text: 'Kurs New Intermediate Grammar został specjalnie stworzony dla osób, które chcą rozwijać swoje kompetencje językowe na poziomie średnio zaawansowanym. Skupia się na praktycznych lekcjach, które obejmują konkretne zagadnienia gramatyczne. Kurs zawiera 98 lekcji.',
  },
  {
    name: 'Hot off the press',
    level: 'Poziom zróżnicowany · lekcje indywidualne',
    popular: false,
    text: 'Kurs Hot off the press koncentruje się na bieżących tematach i krótkich artykułach prasowych. Co tydzień dodajemy 3 lekcje na 3 poziomach (A1, A2-B1, B2-C1), dotyczące tego samego tematu, poznając przy tym nowe słownictwo i struktury. Jest to idealny kurs dla osób, które najłatwiej znajdują motywację do nauki rozmawiając w obrębie zagadnień z kręgu swoich zainteresowań.',
  },
  {
    name: 'Kurs Poziomujący',
    level: 'Wszystkie poziomy · e-learning',
    popular: true,
    text: 'Darmowy kurs poziomujący dostępny na naszej platformie, dzięki któremu dowiesz się, jak dobrze posługujesz się językiem angielskim.',
  },
  {
    name: 'Phrasal Verbs',
    level: 'Poziom średniozaawansowany B2 · e-learning',
    popular: false,
    text: 'Kurs na poziomie B2–C2 dla uczniów, którym zależy na intensywnym rozwoju słownictwa. Każda lekcja wprowadza nowe czasowniki frazalne (tzw. phrasal verbs) z innego zakresu słownictwa, na dalszym etapie uczeń ćwiczy używanie ich w kontekście i rozmowie. Tematy obejmują np. dom, pracę, wakacje, sport, zakupy, gotowanie, transport, zdrowie, rodzinę, zwierzęta, technologię, relacje, edukację i pieniądze.',
  },
] as const

/** Quote, name, role, photo in /public/reviews (Tutlo's own testimonials, 96 px copies). */
const REVIEWS = [
  [
    'To, co mnie przekonało do Tutlo, to możliwość odbywania lekcji i rozmowy z lektorami z całego świata. Uczę się rozumieć różne akcenty, nie tylko brytyjski i amerykański, więc czuję się bardziej przygotowany na realny kontakt z językiem.',
    'Marek',
    'prawnik',
    'marek',
  ],
  [
    'W Tutlo wchodzę na platformę, sprawdzam, który z moich ulubionych lektorów jest dostępny, i zaczynam moje konwersacje. Efekty zauważyli już moi nauczyciele i współpracownicy.',
    'Edyta',
    'nauczycielka',
    'edyta',
  ],
  [
    'W Tutlo uczę się od trzech miesięcy. Jestem bardzo zadowolona z możliwości łączenia się na lekcję, kiedy chcę, szczególnie że mam bardzo napięty grafik. Efekty przyszły szybko, szczególnie w swobodzie komunikowania się.',
    'Ania',
    'mama',
    'ania',
  ],
  [
    'Ostatnio zachwyciła mnie możliwość nauki z lektorem AI. Choć byłem sceptyczny, uważam, że warto iść z duchem czasu i otwierać się na nowe technologie. Polecam takie rozwiązanie. Interakcje są naturalne, a odpowiedzi zawsze dostosowane do moich potrzeb. Idealne uzupełnienie tradycyjnych lekcji.',
    'Ksawery',
    'inżynier',
    'ksawery',
  ],
  [
    'Na początku nie wierzyłem, że tak krótkie lekcje mogą dać efekty, ale już po kilku tygodniach w Tutlo przekonałem się, że to działa! Lekcje są intensywne, ale przyjemne, a to, że mogę zacząć naukę nawet z kawiarni, jest dla mnie bezcenne. Polecam każdemu, kto szuka nowoczesnej i skutecznej metody nauki angielskiego.',
    'Jakub',
    'przedsiębiorca',
    'jakub',
  ],
  [
    'Od kiedy zaczęłam korzystać z Tutlo, moje podejście do nauki angielskiego zmieniło się o 180 stopni! Najbardziej cenię sobie elastyczność — mogę uczyć się o każdej porze dnia, bez potrzeby dostosowywania się do sztywnego grafiku. Lekcje są szybkie, konkretne i dają świetne rezultaty. W końcu nie boję się mówić po angielsku!',
    'Magdalena',
    'specjalistka HR',
    'magdalena',
  ],
] as const

const FAQ = [
  [
    'Jak zacząć naukę angielskiego dla początkujących online w Tutlo?',
    'To proste – kliknij „Umów bezpłatną lekcję próbną” i odpowiedz na kilka krótkich pytań o cel i grafik. Potem wybierasz termin spotkania online: porozmawiasz z przewodnikiem i odbędziesz lekcję próbną z lektorem. Na koniec dostaniesz plan nauki i propozycję kursu z ceną, a jeśli zdecydujesz się zacząć, możesz od razu rozpocząć kurs angielskiego od podstaw.',
  ],
  [
    'Czy kurs języka angielskiego od podstaw jest odpowiedni dla osób zupełnie początkujących?',
    'Tak! Nasze kursy są zaprojektowane tak, aby nawet osoby, które nigdy wcześniej nie uczyły się angielskiego, mogły skutecznie zacząć naukę.',
  ],
  [
    'Ile czasu zajmuje przejście z poziomu A1 na wyższe poziomy?',
    'Czas potrzebny na przejście z poziomu A1 na wyższe poziomy zależy od wielu czynników, takich jak intensywność nauki, metoda nauczania oraz indywidualne predyspozycje ucznia. Zazwyczaj regularna nauka angielskiego dla początkujących podczas kursu, samodzielne powtarzanie materiału i praktyka mogą przyspieszyć ten proces. Na przykład osiągnięcie poziomu B1 może zająć od 6 miesięcy do roku, podczas gdy dojście do poziomu C1 może wymagać kilku lat intensywnej nauki.',
  ],
  [
    'Ile czasu zajmuje nauka podstawowego słownictwa?',
    'Opanowanie zakresu słownictwa na poziomie A1 może zająć od kilku tygodni do kilku miesięcy, w zależności od intensywności nauki. Kluczowe jest powtarzanie i stosowanie nowych słów w kontekście, np. podczas prostych dialogów lub ćwiczeń. Regularne korzystanie z podstawowego słownictwa pozwala szybciej osiągnąć postępy.',
  ],
  [
    'Czy mogę uczyć się na telefonie?',
    'Oczywiście! Nasza platforma działa na komputerach, tabletach i smartfonach, więc uczysz się tam, gdzie Ci wygodnie. Na samą lekcję próbną wchodzisz przez przeglądarkę – potrzebujesz tylko kamery i mikrofonu.',
  ],
] as const
